import { useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Grid, Card, CircularProgress } from '@mui/material';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import VerticalBarChart from 'examples/Charts/BarCharts/VerticalBarChart';
import ReportFilters from 'components/ReportFilters/ReportFilters';

import usePaginationHook from 'hooks/usePaginationHook';
import useReportsHook from 'hooks/useReportsHook';

import { recurringRevenueColumns } from 'constants/columns.constants';

import { formatDate, formatCurrency } from 'helpers/formatData';

function RecurringRevenue() {
  const [selectedDuration, setSelectedDuration] = useState(null);

  const {
    currentPage,
    setCurrentPage,
    perPage,
    setPerPage,
    sortBy,
    setSortBy,
    searchTerm,
  } = usePaginationHook();

  const {
    fetchReportsData,
    reportsList,
    isLoading,
    fetchRecurringRevenueCharts,
    isFetchingChart = false,
    chartResponse = [],
    onDownloadCSV,
  } = useReportsHook({
    currentPage,
    perPage,
    sortBy,
    searchTerm,
    reportType: 'recurring-revenue',
    duration: selectedDuration,
    // columns: recurringRevenueColumns,
  });

  const onFetchDataDebounced = debounce(fetchReportsData, 0);

  useEffect(() => {
    fetchRecurringRevenueCharts();

    return () => {};
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedDuration]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, perPage, searchTerm, selectedDuration]);

  const onChangeDuration = (value) => {
    setSelectedDuration(value);
  };

  const rows = useMemo(() => {
    return reportsList?.reportsData?.length > 0
      ? reportsList?.reportsData?.map((report) => ({
          id: report?.id,
          date: formatDate(report?.date),
          propertyName: report?.propertyName ?? 'NA',
          propertyType: report?.propertyType ?? 'NA',
          revenueFromPriceOfProperty: formatCurrency(
            report?.revenueFromPriceOfProperty ?? 0
          ),
          revenueFromRents: formatCurrency(report?.revenueFromRents ?? 0),
          totalRevenue: formatCurrency(report?.totalRevenue ?? 0),
        }))
      : [];
  }, [reportsList?.reportsData]);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Recurring Revenue
              </MDTypography>
            </MDBox>
            <MDBox mt={5} px={2}>
              {isFetchingChart ? (
                <MDBox
                  sx={{
                    width: '100%',
                    minHeight: '200px',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <CircularProgress color="info" />
                </MDBox>
              ) : (
                <VerticalBarChart
                  icon={{ color: 'info', component: 'leaderboard' }}
                  title="Recurring Revenue Report"
                  chart={{
                    labels: [
                      'Jan',
                      'Feb',
                      'Mar',
                      'Apr',
                      'May',
                      'Jun',
                      'Jul',
                      'Aug',
                      'Sep',
                      'Oct',
                      'Nov',
                      'Dec',
                    ],
                    datasets: [
                      {
                        label: 'Recurring Revenue by month',
                        color: 'dark',
                        data: chartResponse,
                      },
                    ],
                  }}
                  tooltip={{
                    callbacks: {
                      label: (context) => {
                        let label = context?.dataset?.label ?? '';

                        if (label) {
                          label += ': ';
                        }

                        if (context?.parsed?.y !== null) {
                          label += `${formatCurrency(context?.parsed?.y ?? 0)}`;
                        }

                        return label;
                      },
                    },
                  }}
                />
              )}
            </MDBox>
            <ReportFilters
              onChangeDuration={onChangeDuration}
              onDownloadCSV={onDownloadCSV}
            />
            <MDBox
              pt={3}
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{ columns: recurringRevenueColumns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: perPage }}
                showTotalEntries
                canSearch={false}
                // searchTerm={searchTerm}
                // setSearchTerm={setSearchTerm}
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={reportsList?.totalPage}
                totalRecords={reportsList?.totalCount}
                // hiddenColumns={['id']}
                queryPageSortBy={[sortBy]}
                setSortBy={setSortBy}
                isLoading={isLoading}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default RecurringRevenue;
